import { Formik } from 'formik'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { StateMachineState } from '../../ProgramStateMachine.types'

import { Button, Flex, FormField, MultiSelect, Select, TextInput, Typography } from '@percent/lemonade'

interface GroupFormValues {
  name: string
  memberIds: string[]
  displayMode: string
  tableType: string
}

export interface GroupEditFormProps {
  allStates: StateMachineState[]
  groupName?: string
  existingGroupNames: string[]
  onSubmit: (values: {
    name: string
    memberIds: string[]
    displayMode: string
    tableType: string
    previousName?: string
  }) => void
  onDelete?: () => void
  onCancel: () => void
}

export function GroupEditForm({
  allStates,
  groupName,
  existingGroupNames,
  onSubmit,
  onDelete,
  onCancel
}: Readonly<GroupEditFormProps>) {
  const { t } = useTranslation()
  const isEditMode = !!groupName

  const displayModeOptions = [
    { value: 'enabled', label: t('stateMachine.stateEdit.displayModeEnabled') },
    { value: 'disabled', label: t('stateMachine.stateEdit.displayModeDisabled') },
    { value: 'hidden', label: t('stateMachine.stateEdit.displayModeHidden') }
  ]

  const tableTypeOptions = [
    { value: 'task', label: t('stateMachine.stateEdit.tableTypeTask') },
    { value: 'task_with_score', label: t('stateMachine.stateEdit.tableTypeTaskWithScore') },
    { value: 'multiple_tasks', label: t('stateMachine.stateEdit.tableTypeMultipleTasks') },
    { value: 'rejected', label: t('stateMachine.stateEdit.tableTypeRejected') },
    { value: 'status', label: t('stateMachine.stateEdit.tableTypeStatus') },
    { value: 'disbursement', label: t('stateMachine.stateEdit.tableTypeDisbursement') }
  ]

  const stageOptions = useMemo(
    () => allStates.map(s => ({ value: s.id, label: s.metadata?.displayKey || s.id })),
    [allStates]
  )

  const initialValues: GroupFormValues = useMemo(() => {
    if (!groupName) return { name: '', memberIds: [], displayMode: 'enabled', tableType: '' }

    const members = allStates.filter(s => s.metadata?.displayGroup === groupName)
    const firstDisplayMode = members[0]?.metadata?.displayMode ?? 'enabled'
    const firstTableType = members[0]?.metadata?.tableType ?? ''

    return {
      name: groupName,
      memberIds: members.map(s => s.id),
      displayMode: firstDisplayMode,
      tableType: firstTableType
    }
  }, [groupName, allStates])

  const validate = (values: GroupFormValues) => {
    const errors: Record<string, string> = {}

    if (!values.name.trim()) {
      errors.name = t('stateMachine.groupEdit.validation.nameRequired')
    } else if (values.name !== groupName && existingGroupNames.includes(values.name)) {
      errors.name = t('stateMachine.groupEdit.validation.nameDuplicate')
    }

    if (values.memberIds.length === 0) {
      errors.memberIds = t('stateMachine.groupEdit.validation.stagesRequired')
    }

    return errors
  }

  return (
    <Formik<GroupFormValues>
      initialValues={initialValues}
      validate={validate}
      onSubmit={values => {
        onSubmit({
          name: values.name,
          memberIds: values.memberIds,
          displayMode: values.displayMode,
          tableType: values.tableType,
          previousName: isEditMode ? groupName : undefined
        })
      }}
      enableReinitialize
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue, submitForm }) => (
        <Flex direction="column" flex={1} miw={0} mih={0}>
          <Flex direction="column" gap="md" flex={1} mih={0} pb={8} style={{ overflowY: 'auto' }}>
            <FormField
              label={t('stateMachine.groupEdit.groupName')}
              status={touched.name && errors.name ? 'danger' : 'default'}
              statusMessage={touched.name && errors.name ? errors.name : ''}
              necessity="required"
            >
              <TextInput
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="group-name"
              />
            </FormField>

            <FormField
              label={t('stateMachine.groupEdit.stages')}
              status={touched.memberIds && errors.memberIds ? 'danger' : 'default'}
              statusMessage={touched.memberIds && typeof errors.memberIds === 'string' ? errors.memberIds : ''}
              necessity="required"
            >
              <MultiSelect
                name="memberIds"
                options={stageOptions}
                defaultValue={stageOptions.filter(o => values.memberIds.includes(o.value))}
                onChange={selected =>
                  setFieldValue(
                    'memberIds',
                    selected.map(o => o.value)
                  )
                }
                placeholder={t('stateMachine.groupEdit.selectStages')}
              />
            </FormField>

            <FormField label={t('stateMachine.groupEdit.displayType')}>
              <Select
                name="displayMode"
                options={displayModeOptions}
                defaultValue={displayModeOptions.find(o => o.value === values.displayMode) ?? displayModeOptions[0]}
                onChange={option => setFieldValue('displayMode', option?.value ?? 'enabled')}
              />
            </FormField>

            <FormField label={t('stateMachine.stateEdit.tableType')}>
              <Select
                name="tableType"
                options={tableTypeOptions}
                defaultValue={tableTypeOptions.find(o => o.value === values.tableType)}
                onChange={option => setFieldValue('tableType', option?.value ?? '')}
                placeholder={t('stateMachine.stateEdit.selectTableType')}
              />
            </FormField>

            {isEditMode && existingGroupNames.includes(values.name) && values.name !== groupName && (
              <Typography style={{ color: '#f59e0b', fontSize: 12 }}>
                {t('stateMachine.groupEdit.validation.nameDuplicate')}
              </Typography>
            )}

            {isEditMode && onDelete && (
              <Flex>
                <Button variant="reject" onClick={onDelete} data-testid="group-edit-delete">
                  {t('stateMachine.groupEdit.deleteGroup')}
                </Button>
              </Flex>
            )}
          </Flex>

          <Flex
            gap="xs"
            justify="flex-end"
            pt={12}
            style={{ flexShrink: 0, borderTop: '1px solid #e2e8f0' }}
            data-testid="group-edit-footer"
          >
            {onCancel && (
              <Button variant="secondary" onClick={onCancel} data-testid="group-edit-cancel">
                {t('button.cancel')}
              </Button>
            )}
            <Button variant="primary" onClick={submitForm} data-testid="group-edit-submit">
              {isEditMode ? t('button.save') : t('stateMachine.groupEdit.addGroup')}
            </Button>
          </Flex>
        </Flex>
      )}
    </Formik>
  )
}
